
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import Tabs from '@/common/components/UI/Tabs/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'

// @module components
import TtnTMSFilters from '@/modules/TMSModule/components/TtnTMSFilters/index.vue'
import TtnEditModal from '@/modules/TMSModule/components/TtnEditModal/index.vue'
import TtnsTMSRow from '@/modules/TMSModule/components/TtnsTMSRow/index.vue'

// @types
import { GetTtnsTMSParams, TtnComment, TtnTMS } from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'

// @composable
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { TabT } from '@/core/types/components.types'

const TABLE_COLUMNS = [
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'ttnNumber',
        name: 'Номер ТТН',
    },
    {
        key: 'tripNumber',
        name: 'Номер рейса',
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'createdAt',
        name: 'Дата создания'
    },
    {
        key: 'expectedDate',
        name: 'Дата прибытия план',
    },
    {
        key: 'arrivalDate',
        name: 'Дата прибытия факт',
    },
    {
        key: 'countExpectedInvoices',
        name: 'Кол-во накладных отправлено',
    },
    {
        key: 'countActualInvoices',
        name: 'Кол-во накладных принято',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'timer',
        name: 'Время до прибытия',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'transportType',
        name: 'Транспортировка авто',
    },
]

export default defineComponent({
    components: {
        TtnTMSFilters,
        'ttns-tms-row': TtnsTMSRow,
        NewTable,
        NewButton,
        Loader,
        NewLimit,
        NewPagination,
        ButtonDropdown,
        NewModal,
        NewSelect,
        Tabs,
        NewInput,
        TtnEditModal,
    },
    setup() {
        const { saveFile } = useFile()
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const tabs: TabT[] = [
            { id: 1, name: 'Рейсы', path: '/tms/trips' },
            { id: 2, name: 'ТТН', path: '/tms/ttns' },
        ]

        const state = reactive({
            list: [] as TtnTMS[],
            is_excel_loading: false,
            is_loading: false,
            is_failed: false,
            filters: {
                ttnNumber: null as string | null,
                tripNumber: null as string | null,
                fromCityId: null as number | null,
                toCityId: null as number | null,
                transportTypeIds: [] as number[],
                statusIds: [] as number[],
                commentIds: [] as number[],
                dateFrom: null as string | null,
                dateTo: null as string | null,
            },
            pagination: {
                page: 1,
                limit: 50,
                total_pages: 1,
                total_items: 20,
            },
        })

        const cancel_state = reactive({
            is_open: false,
            is_loading: false,
            //
            commentId: null as number | null,
            tripId: null as number | null,
        })

        const edit_state = reactive({
            is_open: false,
            item: null as TtnTMS | null,
        })

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TtnComment[],
        })

        const CancelTripValidation = {
            commentId: { required },
        }
        const state_form$ = useVuelidate(CancelTripValidation, cancel_state)

        //

        const loadTtns = async () => {
            try {
                state.is_loading = true

                const params: GetTtnsTMSParams = {
                    page: state.pagination.page,
                    limit: state.pagination.limit,
                    ...state.filters,
                }

                const response = await tmsAPI.getTtns(params)
                state.list = response.data.data
                state.pagination.total_items = response.data.meta.total
                state.pagination.total_pages = response.data.meta.last_page

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersChanged = (filters: GetTtnsTMSParams) => {
            state.filters.ttnNumber = filters.ttnNumber
            state.filters.tripNumber = filters.tripNumber
            state.filters.fromCityId = filters.fromCityId
            state.filters.toCityId = filters.toCityId
            state.filters.dateFrom = filters.dateFrom
            state.filters.dateTo = filters.dateTo
            state.filters.transportTypeIds = filters.transportTypeIds
            state.filters.statusIds = filters.statusIds
            state.filters.commentIds = filters.commentIds

            state.pagination.page = 1

            loadTtns()
        }

        const onFiltersReset = () => {
            state.filters.ttnNumber = null
            state.filters.tripNumber = null
            state.filters.fromCityId = null
            state.filters.toCityId = null
            state.filters.dateFrom = null
            state.filters.dateTo = null
            state.filters.transportTypeIds = []
            state.filters.statusIds = []
            state.filters.commentIds = []

            state.pagination.page = 1

            loadTtns()
        }

        const downloadReportTtns = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    ...state.filters,
                }

                const response = await tmsAPI.downloadReportTtns(params)
                saveFile(response.data, `Отчет по рейсам`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const onLimitChange = (new_limit: number) => {
            state.pagination.limit = new_limit
            loadTtns()
        }

        const onChangePage = (new_page: number) => {
            state.pagination.page = new_page
            loadTtns()
        }

        const toggleCancel = (trip_id?: number) => {
            cancel_state.is_open = !cancel_state.is_open
            cancel_state.commentId = null
            cancel_state.tripId = trip_id ? trip_id : null
        }

        const openEdit = (item: TtnTMS) => {
            edit_state.is_open = true
            edit_state.item = { ...item }
        }

        const closeEdit = () => {
            edit_state.is_open = false
            edit_state.item = null

            loadTtns()
        }

        const setComment = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !cancel_state.tripId) return

            try {
                cancel_state.is_loading = true

                const payload = {
                    commentId: cancel_state.commentId as number,
                }

                await tmsAPI.setComment(cancel_state.tripId, payload)

                toggleCancel()
                loadTtns()
                notifications.notify({ title: 'Комментарий добавлен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                cancel_state.is_loading = false
            }
        }

        const loadComments = async () => {
            try {
                comments_state.is_loading = true

                const response = await tmsAPI.getTtnComments()

                comments_state.list = response.data.data
                comments_state.is_failed = false
            } catch (error) {
                console.error(error)
                comments_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка комментариев произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        //

        onMounted(() => {
            loadComments()
            loadTtns()
        })

        //

        return {
            state,
            cancel_state,
            edit_state,
            comments_state,
            state_form$,
            //
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            loadTtns,
            downloadReportTtns,
            havePermissions,
            toggleCancel,
            openEdit,
            closeEdit,
            setComment,
            //
            TABLE_COLUMNS,
            tabs,
        }
    },
})
